import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
  type ColumnDef,
  type ExpandedState,
} from '@tanstack/react-table';
import { Camera, ExternalLink, Search } from 'lucide-react';
import { fetchNewItemVendors, fetchNewItemsProcessed } from '../api/new_items';
import type { NewItem, NewItemReviewStatus } from '../api/new_items';
import type { VendorOption } from '../api/checks';
import ExportButton from '../components/ExportButton';
import NewItemPayloadPanel from '../components/NewItemPayloadPanel';
import { HeaderActions } from '../components/HeaderActions';
import { NewItemReviewStatusBadge } from '../components/badges';
import { MultiSelect } from '../components/ui/multi-select';
import { FilterSelect } from '../components/ui/filter-select';
import { DataTable } from '../components/data-table/DataTable';
import { ACTIONS_COL_WIDTH, NsIdLink, expandColumn } from '../components/data-table/columns';
import { RowActionMenu, RowActions, type RowMenuItem } from '../components/data-table/RowActions';
import {
  ColumnsMenu,
  FilterBar,
  FilterRow,
  PageHeading,
  ResetFiltersButton,
  TablePagination,
} from '../components/data-table/TableChrome';
import { NS_BASE_URL } from '../config/external_links';
import { fmtDateTime, fmtPrice, fmtVendor } from '../utils/format';

const PAGE_SIZES = [50, 100, 250, 500, 1000] as const;

const OUTCOMES = [
  { value: '',              label: 'All' },
  { value: 'created',       label: 'Created',      dot: 'bg-green-500' },
  { value: 'postponed',     label: 'Skipped for now', dot: 'bg-sky-500' },
  { value: 'skipped',       label: 'Skipped forever', dot: 'bg-amber-500' },
  { value: 'create_failed', label: 'Failed',       dot: 'bg-red-600'   },
  // BOT-430: stock-rule auto-skip verdict, distinct from operator "Skipped for now" / "Skipped forever".
  { value: 'auto_skipped',  label: 'Auto-skipped', dot: 'bg-slate-400' },
];

export default function NewItemsProcessedPage() {
  const [vendorOptions, setVendorOptions] = useState<VendorOption[]>([]);
  const [vendorCodes, setVendorCodes]     = useState<string[]>([]);
  const [outcome, setOutcome] = useState<'' | NewItemReviewStatus>('');
  const [page, setPage]       = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [items, setItems]     = useState<NewItem[]>([]);
  const [total, setTotal]     = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({});

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setExpanded({});
    try {
      const data = await fetchNewItemsProcessed({
        vendor_codes: vendorCodes.length ? vendorCodes : undefined,
        review_status: outcome || undefined,
        page,
        per_page: perPage,
      });
      setItems(data.items);
      setTotal(data.total);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [vendorCodes, outcome, page, perPage]);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    fetchNewItemVendors().then(setVendorOptions).catch(() => setVendorOptions([]));
  }, []);

  const columns = useMemo<ColumnDef<NewItem>[]>(() => [
    expandColumn<NewItem>(),
    {
      id: 'sku',
      size: 230,
      enableHiding: false,
      meta: { label: 'SKU' },
      header: 'SKU',
      cell: ({ row }) => (
        <>
          <div className="whitespace-nowrap font-mono text-[12.5px] font-medium">{row.original.sku}</div>
          <div className="max-w-[240px] truncate text-xs text-muted-foreground">
            {row.original.display_name ?? row.original.item_name ?? ''}
          </div>
        </>
      ),
    },
    {
      id: 'vendor',
      meta: { label: 'Vendor' },
      header: 'Vendor',
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-zinc-700">{fmtVendor(row.original.vendor_name)}</span>
      ),
    },
    {
      id: 'manufacturer',
      meta: { label: 'Manufacturer' },
      header: 'Manufacturer',
      cell: ({ row }) => <span className="whitespace-nowrap text-zinc-700">{row.original.manufacturer ?? '—'}</span>,
    },
    {
      id: 'purchase',
      meta: { label: 'Purchase cost', align: 'right' },
      header: 'Purchase cost',
      cell: ({ row }) => (
        <span className="whitespace-nowrap font-medium">
          {row.original.vendor_price != null ? fmtPrice(row.original.vendor_price) : '—'}
        </span>
      ),
    },
    {
      id: 'vendorStock',
      meta: { label: 'Vendor stock', align: 'right' },
      header: 'Vendor stock',
      cell: ({ row }) => <span className="text-zinc-700">{row.original.vendor_stock ?? '—'}</span>,
    },
    {
      id: 'outcome',
      meta: { label: 'Outcome' },
      header: 'Outcome',
      cell: ({ row }) => <NewItemReviewStatusBadge status={row.original.review_status} />,
    },
    {
      id: 'ns',
      meta: { label: 'NS ID' },
      header: 'NS ID',
      cell: ({ row }) =>
        row.original.ns_product_id ? (
          <NsIdLink id={row.original.ns_product_id} />
        ) : (
          <span className="text-zinc-300">—</span>
        ),
    },
    {
      id: 'reviewer',
      meta: { label: 'Reviewed by' },
      header: 'Reviewed by',
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-muted-foreground">{row.original.reviewer_email ?? '—'}</span>
      ),
    },
    {
      id: 'date',
      meta: { label: 'Date' },
      header: 'Date',
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-muted-foreground">
          {fmtDateTime(
            row.original.ns_created_at ?? row.original.reviewed_at ?? row.original.updated_at
          )}
        </span>
      ),
    },
    {
      id: 'error',
      meta: { label: 'Error' },
      header: 'Error',
      cell: ({ row }) =>
        row.original.ns_create_error ? (
          <span className="block max-w-[280px] truncate text-xs text-red-600" title={row.original.ns_create_error}>
            {row.original.ns_create_error}
          </span>
        ) : (
          <span className="text-zinc-300">—</span>
        ),
    },
    {
      id: 'actions',
      size: ACTIONS_COL_WIDTH,
      enableHiding: false,
      meta: { label: 'Actions', align: 'right', padding: 'tight' },
      header: 'Actions',
      cell: ({ row }) => {
        const item = row.original;
        const menuItems: RowMenuItem[] = [];
        // Only created rows exist in tbnsdata — no detail target before that.
        if (item.ns_product_id) {
          menuItems.push({
            label: 'Details',
            icon: Search,
            href: `/all-items/${item.ns_product_id}?from=new-processed`,
          });
          menuItems.push({
            label: 'Open in NetSuite',
            icon: ExternalLink,
            href: `${NS_BASE_URL}${item.ns_product_id}`,
          });
        }
        if (item.import_payload.url) {
          menuItems.push({ label: 'Open vendor page', icon: ExternalLink, href: item.import_payload.url });
        }
        if (item.screenshot_url) {
          menuItems.push({ label: 'View screenshot', icon: Camera, href: item.screenshot_url });
        }
        return (
          <RowActions>
            <RowActionMenu items={menuItems} />
          </RowActions>
        );
      },
    },
  ], []);

  const table = useReactTable({
    data: items,
    columns,
    getRowId: row => String(row.id),
    state: { expanded, columnVisibility },
    onExpandedChange: setExpanded,
    onColumnVisibilityChange: setColumnVisibility,
    getRowCanExpand: () => true,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    initialState: { columnPinning: { left: ['expand', 'sku'], right: ['actions'] } },
  });

  const totalPages = Math.max(Math.ceil(total / perPage), 1);

  return (
    <>
      <HeaderActions>
        <ExportButton
          resource="new_items_processed"
          filters={{
            vendor_codes: vendorCodes.length ? vendorCodes : undefined,
            review_status: outcome || undefined,
          }}
        />
      </HeaderActions>

      <PageHeading title="New Items — Processed" count={total} />

      <FilterBar>
        <FilterRow>
          <MultiSelect
            label="Vendor"
            options={vendorOptions.map(v => ({ value: v.code, label: fmtVendor(v.name) }))}
            selected={vendorCodes}
            onChange={next => { setVendorCodes(next); setPage(1); }}
          />
          <FilterSelect
            label="Outcome"
            options={OUTCOMES}
            value={outcome}
            onChange={v => { setOutcome(v as '' | NewItemReviewStatus); setPage(1); }}
            contentClassName="w-[220px]"
          />
          <div className="ml-auto flex items-center gap-2">
            {(vendorCodes.length > 0 || outcome) && (
              <ResetFiltersButton onClick={() => { setVendorCodes([]); setOutcome(''); setPage(1); }} />
            )}
            <ColumnsMenu table={table} />
          </div>
        </FilterRow>
      </FilterBar>

      <DataTable
        table={table}
        loading={loading}
        error={error}
        emptyMessage="No processed items"
        renderExpanded={row => (
          <NewItemPayloadPanel
            payload={row.original.import_payload}
            dimsSource={row.original.dims_source}
            dimsSourceDetail={row.original.dims_source_detail}
          />
        )}
      />

      <TablePagination
        page={page}
        totalPages={totalPages}
        total={total}
        perPage={perPage}
        pageSizes={PAGE_SIZES}
        onPageChange={setPage}
        onPerPageChange={n => { setPerPage(n); setPage(1); }}
      />
    </>
  );
}
